import { useState, useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import ReactQuill from 'react-quill';
import { Warning } from '@sweep-io/sweep-design/dist/icons/Warning';
import colors from '@sweep-io/sweep-design/dist/colors';

interface UseCurlyBraceBlockingReturn {
  showBracesWarning: boolean;
  checkAndBlockCurlyBraces: (
    delta: any,
    source: string,
    quillRef: React.MutableRefObject<ReactQuill>,
  ) => boolean;
  WarningTooltip: () => JSX.Element | null;
}

export const useCurlyBraceBlocking = (): UseCurlyBraceBlockingReturn => {
  const [showBracesWarning, setShowBracesWarning] = useState(false);

  // Auto-hide warning after 3 seconds
  useEffect(() => {
    if (showBracesWarning) {
      const timer = setTimeout(() => {
        setShowBracesWarning(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showBracesWarning]);

  const checkAndBlockCurlyBraces = (
    delta: any,
    source: string,
    quillRef: React.MutableRefObject<ReactQuill>,
  ): boolean => {
    // Check if curly braces were typed/pasted (only for text, not for embeds like dynamic fields)
    if (source === 'user' && delta?.ops && quillRef.current) {
      const editor = quillRef.current.getEditor();
      let hasBraces = false;
      let braceCount = 0;

      // Check for braces in text insertions only (not in embeds)
      delta.ops.forEach((op: any) => {
        if (op.insert && typeof op.insert === 'string' && /[{}]/.test(op.insert)) {
          hasBraces = true;
          // Count how many braces were inserted
          braceCount += (op.insert.match(/[{}]/g) || []).length;
        }
      });

      if (hasBraces) {
        setShowBracesWarning(true);

        // Delete the brace characters that were just added
        const selection = editor.getSelection();
        if (selection && selection.index >= braceCount) {
          // Delete the characters that were just inserted
          editor.deleteText(selection.index - braceCount, braceCount);
        }
        return true;
      }
    }
    return false;
  };

  const WarningTooltip = () => {
    if (!showBracesWarning) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          top: '-48px',
          left: '0',
          right: '0',
          zIndex: 1500,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Alert
          icon={<Warning size="medium" />}
          sx={{
            background: colors.sun[300],
            color: colors.night[900],
            alignItems: 'center',
            mr: '8px',
          }}
          severity="warning"
          onClose={() => setShowBracesWarning(false)}
        >
          Curly braces {'{ }'} are reserved for dynamic fields and cannot be entered manually
        </Alert>
      </Box>
    );
  };

  return {
    showBracesWarning,
    checkAndBlockCurlyBraces,
    WarningTooltip,
  };
};
